import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import FormField, { inputStyle } from '../components/FormField';
import Button from '../components/Button';
import * as setupApi from '../services/setupApi';

export default function SetupPage() {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [adminFullName, setAdminFullName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [alreadySetUp, setAlreadySetUp] = useState(false);
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (adminPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (adminPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await setupApi.initializeCompany({
        companyName,
        adminFullName,
        adminEmail,
        adminPassword,
      });
      setSuccess(result);
    } catch (err) {
      if (err.code === 'FORBIDDEN') {
        // This is the expected, non-scary outcome when setup already ran —
        // point straight to login rather than showing a raw permission error.
        setAlreadySetUp(true);
      } else {
        setError(err.message || 'Could not complete setup. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg)', padding: 'var(--space-5)' }}>
        <div className="card" style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-3)' }}>
            <span style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--color-accent-soft)', color: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700 }}>
              ✓
            </span>
          </div>
          <h2 style={{ marginBottom: 8 }}>You're all set</h2>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-5)' }}>
            <strong>{success.company.name}</strong> has been created. Sign in with{' '}
            <strong>{success.admin.email}</strong> and the password you just chose.
          </p>
          <Button style={{ width: '100%' }} onClick={() => navigate('/login')}>
            Go to sign in
          </Button>
        </div>
      </div>
    );
  }

  if (alreadySetUp) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg)', padding: 'var(--space-5)' }}>
        <div className="card" style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <h2 style={{ marginBottom: 8 }}>Setup already complete</h2>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-5)' }}>
            A company account already exists on this system. If you don't have login details,
            ask whoever set this up (your company administrator) for an invite — new team members
            are added from inside the app under User Management, not through this page.
          </p>
          <Button style={{ width: '100%' }} onClick={() => navigate('/login')}>
            Go to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg)', padding: 'var(--space-5)' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ marginBottom: 'var(--space-5)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--space-2)' }}>
            <span className="tile-mark" aria-hidden="true" />
            <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
              Dispatch Analysis
            </span>
          </div>
          <h2 style={{ fontSize: 'var(--font-size-lg)', marginBottom: 4 }}>Set up your company</h2>
          <p style={{ color: 'var(--color-text-secondary)', margin: 0, fontSize: 'var(--font-size-sm)' }}>
            This runs once, the first time the software is used, to create your company and its
            first admin account.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="card" style={{ marginBottom: 0 }}>
          {error && (
            <div
              role="alert"
              style={{
                background: 'var(--color-accent-warm-soft)',
                border: '1px solid var(--color-danger)',
                color: 'var(--color-danger)',
                borderRadius: 'var(--radius-sm)',
                padding: 'var(--space-3)',
                fontSize: 'var(--font-size-sm)',
                marginBottom: 'var(--space-4)',
              }}
            >
              {error}
            </div>
          )}

          <FormField label="Company name">
            <input
              type="text"
              required
              autoFocus
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              style={inputStyle}
            />
          </FormField>

          <FormField label="Your full name">
            <input
              type="text"
              required
              value={adminFullName}
              onChange={(e) => setAdminFullName(e.target.value)}
              style={inputStyle}
            />
          </FormField>

          <FormField label="Your email address">
            <input
              type="email"
              required
              autoComplete="username"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              style={inputStyle}
            />
          </FormField>

          <FormField label="Password (min. 8 characters)">
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              style={inputStyle}
            />
          </FormField>

          <FormField label="Confirm password">
            <input
              type="password"
              required
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={inputStyle}
            />
          </FormField>

          <Button type="submit" disabled={submitting} style={{ width: '100%', marginTop: 'var(--space-2)' }}>
            {submitting ? 'Creating your account…' : 'Create company & admin account'}
          </Button>
        </form>

        <p style={{ textAlign: 'center', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-5)' }}>
          Already set up? <Link to="/login" style={{ color: 'var(--color-accent)' }}>Sign in instead</Link>
        </p>
      </div>
    </div>
  );
}
